import { useState } from 'preact/hooks';
import { IconWrap } from '../components/Icon.tsx';
import type { IconName } from '../components/Icon.tsx';
import type { PageId } from '../components/Nav.tsx';
import { closureForDate, schoolDayPosition } from '../lib/calendar.ts';
import { addDays, frLongToday, parseLocalDate, toLocalISO, todayISO } from '../lib/dates.ts';
import { slotTimeLabel } from '../lib/slots.ts';
import { studentDisplayName, studentNumbers } from '../lib/student-roster.ts';
import { live, useStore } from '../store/store.ts';
import { openModal } from '../ui/ui-store.ts';
import { PostitModal } from '../features/misc/SimpleModals.tsx';
import { openQuickNote } from '../features/observations/QuickNoteModal.tsx';

const SHORTCUTS: readonly {
  icon: IconName;
  title: string;
  sub: string;
  page: PageId;
  bg: string;
}[] = [
  { icon: 'cal', title: 'Mon semainier', sub: 'Voir et modifier ma semaine', page: 'week', bg: '#e8f3fc' },
  { icon: 'eye', title: 'Observations', sub: 'Suivre mes élèves', page: 'class', bg: '#f1ebfb' },
  { icon: 'check', title: 'Évaluations', sub: 'Saisir et consulter', page: 'class', bg: '#faeaf0' },
  { icon: 'meeting', title: 'Réunions', sub: 'Plannings et comptes rendus', page: 'more', bg: '#fff7da' },
  { icon: 'target', title: 'Compétences', sub: 'Suivi et progression', page: 'follow', bg: '#e5f7f5' },
  // The home task card is gone; this tile is now the only way into the page.
  { icon: 'check', title: 'Mes tâches', sub: 'Calendrier des tâches', page: 'tasks', bg: '#eaf6ee' },
];

export function HomePage({ onNavigate }: { onNavigate: (page: PageId) => void }) {
  const teacher = useStore((s) => s.settings.teacher);

  // Each `live()` call allocates a new array. Kept outside the selectors:
  // useSyncExternalStore compares snapshots by reference, so a selector that
  // returns a fresh array re-renders forever and hangs the tab.
  const postits = live(useStore((s) => s.postits));
  const lessons = live(useStore((s) => s.lessons));
  const observations = live(useStore((s) => s.observations));
  const students = live(useStore((s) => s.students));
  const evaluations = live(useStore((s) => s.evaluations));
  const deletePostit = useStore((s) => s.deletePostit);

  const tasks = live(useStore((s) => s.tasks));
  const toggleTask = useStore((s) => s.toggleTask);

  // The day card browses forward and back; "Aujourd'hui" brings it home. Kept
  // as an ISO date rather than an offset so it compares directly against
  // `task.due` and survives a render crossing midnight.
  const [dayIso, setDayIso] = useState(todayISO);
  const day = parseLocalDate(dayIso);
  const isToday = dayIso === todayISO();

  // The day's lessons. The original listed the first four lessons of the
  // current *week* under a heading saying "Aujourd'hui", and labelled them with
  // consecutive period times regardless of the slot each lesson actually
  // occupied — so the times shown did not belong to the lessons shown. This
  // reads the day's real lessons at their real times.
  const position = schoolDayPosition(day);
  const dayClosure = closureForDate(day);

  const dayLessons =
    position && !dayClosure
      ? lessons
          .filter((l) => l.weekStart === position.weekStart && l.day === position.day)
          .sort((a, b) => a.slot - b.slot)
      : [];

  // Still to do first, done greyed out underneath.
  const dayTasks = tasks.filter((t) => t.due === dayIso);
  const pendingTasks = dayTasks.filter((t) => !t.done);
  const doneTasks = dayTasks.filter((t) => t.done);

  const weekday = day.toLocaleDateString('fr-BE', { weekday: 'long' });

  function shiftDay(offset: number) {
    setDayIso(toLocalISO(addDays(day, offset)));
  }

  const recent = observations.slice().reverse().slice(0, 4);
  const studentById = (id: string) => students.find((s) => s.id === id);
  // The same alphabetical numbering as the class list, so a pupil carries the
  // same number wherever they show up.
  const numbers = studentNumbers(students);

  return (
    <section class="page">
      <div class="hero">
        <div>
          <div class="eyebrow">{frLongToday()}</div>
          <h1>Bonjour, {teacher} !</h1>
        </div>

        <div class="noteCard quickNotes">
          <div class="cardHead">
            <IconWrap name="note" background="#fff7da" />
            {/* Named for what it is, so it cannot be mistaken for the
                "Note rapide" button, which files an observation on a pupil. */}
            <div>
              <h3>Post-it</h3>
              <div class="small muted">Pense-bêtes</div>
            </div>
            <span class="spacer" />
            <button
              class="iconBtn"
              aria-label="Ajouter un post-it"
              onClick={() => openModal('Nouveau post-it', <PostitModal />)}
            >
              +
            </button>
          </div>

          {postits.length ? (
            <div class="postits">
              {postits.slice(0, 3).map((p) => (
                <div key={p.id} class="post" style={{ background: p.color }}>
                  <span>{p.text}</span>
                  <button
                    class="postDelete"
                    aria-label={`Supprimer la note « ${p.text} »`}
                    title="Supprimer cette note"
                    onClick={() => deletePostit(p.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div class="empty">Aucune note.</div>
          )}
        </div>
      </div>

      <div class="shortcuts">
        {SHORTCUTS.map((s) => (
          <button key={s.title} class="shortcut" onClick={() => onNavigate(s.page)}>
            <IconWrap name={s.icon} background={s.bg} />
            <b>{s.title}</b>
            <span class="small muted">{s.sub}</span>
          </button>
        ))}
      </div>

      <div class="grid2">
        <div class="card dayCard">
          <div class="cardHead">
            <IconWrap name="cal" background="#e8f3fc" />
            <div>
              <h3>{isToday ? 'Aujourd’hui' : weekday.charAt(0).toUpperCase() + weekday.slice(1)}</h3>
              <div class="small muted">{frLongToday(day)}</div>
            </div>
            <span class="spacer" />
            <div class="dayNav">
              {!isToday && (
                <button class="btn soft" onClick={() => setDayIso(todayISO())}>
                  Aujourd’hui
                </button>
              )}
              <button class="iconBtn" aria-label="Jour précédent" onClick={() => shiftDay(-1)}>
                ←
              </button>
              <button class="iconBtn" aria-label="Jour suivant" onClick={() => shiftDay(1)}>
                →
              </button>
            </div>
          </div>

          {/* One scroll zone for the whole day: a full timetable plus its tasks
              easily outgrows the card, and the page below must stay reachable. */}
          <div class="dayScroll">
            <div class="dayTasks">
              <div class="dayTasksHead small muted">Tâches du jour</div>
              {dayTasks.length ? (
                [...pendingTasks, ...doneTasks].map((task) => (
                  <label key={task.id} class={`dayTask${task.done ? ' done' : ''}`}>
                    <input
                      type="checkbox"
                      checked={task.done}
                      onChange={() => toggleTask(task.id)}
                    />
                    <span>{task.text}</span>
                  </label>
                ))
              ) : (
                <div class="small muted">Aucune tâche pour ce jour.</div>
              )}
            </div>

            {dayClosure ? (
              <div class="empty">{dayClosure.label}</div>
            ) : dayLessons.length ? (
              dayLessons.map((l) => {
                const [start, end] = slotTimeLabel(l.slot).split(' – ');
                return (
                  <div key={l.id} class="todayItem">
                    <div class="time">
                      {start}
                      <br />
                      <span class="small">{end}</span>
                    </div>
                    <div class="event" style={{ background: l.color }}>
                      <b>{l.title}</b>
                      <div class="small muted">{l.detail || l.subject}</div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div class="empty">
                {isToday ? 'Rien de planifié aujourd’hui.' : 'Rien de planifié ce jour-là.'}
              </div>
            )}
          </div>
        </div>

        <div class="card">
          <div class="cardHead">
            <IconWrap name="eye" background="#f1ebfb" />
            <div>
              <h3>Élèves à suivre</h3>
              <div class="small muted">Dernières observations</div>
            </div>
            <span class="spacer" />
            <button class="iconBtn" aria-label="Ajouter une note rapide" onClick={openQuickNote}>
              +
            </button>
          </div>

          {recent.length ? (
            recent.map((o) => {
              const student = studentById(o.studentId);
              return (
                <div key={o.id} class="studentLine">
                  <div class="avatar">{(student && numbers.get(student.id)) ?? '?'}</div>
                  <div>
                    <b>{student ? studentDisplayName(student.name) : 'Élève'}</b>
                    <div class="small muted">{o.note}</div>
                  </div>
                  <span class="tag">{o.subject || 'Obs.'}</span>
                </div>
              );
            })
          ) : (
            <div class="empty">Ajoute des élèves puis une observation.</div>
          )}
        </div>
      </div>

      <div class="grid3" style={{ marginTop: '16px' }}>
        <div class="card">
          <div class="cardHead">
            <IconWrap name="users" background="#faeaf0" />
            <div>
              <h3>Ma classe en un coup d’œil</h3>
            </div>
          </div>

          <div class="stats" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
            <div class="stat">
              <strong>{students.length}</strong>
              <span class="small muted">Élèves</span>
            </div>
            <div class="stat">
              <strong>{evaluations.length}</strong>
              <span class="small muted">Évaluations</span>
            </div>
            <div class="stat">
              <strong>{observations.length}</strong>
              <span class="small muted">Observations</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
